import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import { Group, TaskFormation } from './styles';
import Accounting from '../../../../component/accounting';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import TaskDetail from './taskDetail';
import TasksPage from './taskPage';
import { SHOW_TASK_GROUPING_TYPE, STATUS_TYPE, TASK_LIST_TYPE } from '../../../../global/constants';
import TaskGrid from './taskGrid';
import { getTaskGroupList } from '../../../../services/taskServices';
import {
  setFilteredTasksList,
  setOtherTaskList,
  setTaskGroupsList,
  setTasksList
} from '../../../../actions/taskActions';
import { getCurrentProjectDetails, getMilestoneList } from '../../../../services/projectServices';
import { captureException } from '../../../../services/logService';
import CalendarView from './calendarView';
import { getWorkspaceProjectPreferenceData } from '../../../../helpers/firebaseHelper';
import { rootStore } from '../../../../mobx/rootStore';
import { syncTasks } from '../../../../services/syncService';
import { observer } from 'mobx-react-lite';
import { WeekDayTask } from '../../../../services/mobxTaskFilterService';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';
import { TaskModel } from '../../../../mobx/models/task';
const Tasks: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [totalCompletedTask, setTotalCompletedTask] = useState<number>(0);
  const [hiddenTask, setHiddenTask] = useState<number>(0);
  const [weekWiseTasks, setWeekWiseTasks] = useState<WeekDayTask[]>([]);
  //Mobx store
  const { projectStore, workspaceStore } = rootStore;
  const { currentProject: projectData } = projectStore;
  const { currentWorkspace: workspace } = workspaceStore;
  // Other variables
  const params: { id: string } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const taskId = new URLSearchParams(location?.search).get('task');

  const calculateTaskCount = useCallback(() => {
    // Get base task lists
    const projectTasks = projectData?.tasks || ([] as TaskModel[]);
    const filteredProjectTasks = projectData?.filteredProjectTasks?.filteredTasks || [];

    // Get parent and completed tasks in one pass
    const { parentTasks, completedTasks, completedSubTasks } = projectTasks.reduce<{
      parentTasks: TaskModel[];
      completedTasks: TaskModel[];
      completedSubTasks: TaskModel[];
    }>(
      (acc, task) => {
        if (!task.parentTaskId) {
          acc.parentTasks.push(task);
          if (task.status && task.statusId) {
            acc.completedTasks.push(task);
          }
        } else if (task.status && task.statusId) {
          acc.completedSubTasks.push(task);
        }
        return acc;
      },
      { parentTasks: [], completedTasks: [], completedSubTasks: [] }
    );

    // Get filtered tasks statuses
    const cancelledTasks = filteredProjectTasks.find((item) => item.status === STATUS_TYPE.CANCELLED)?.tasks || [];
    const currentCompletedParentTasks =
      filteredProjectTasks.find((item) => item.status === STATUS_TYPE.COMPLETED)?.tasks || [];

    // Create lookup maps for faster checks
    const completedParentTaskIds = new Set(currentCompletedParentTasks.map((task) => task.id));
    const openParentTaskIds = new Set(parentTasks.filter((task) => !task.status).map((task) => task.id));
    const allParentTaskIds = new Set(parentTasks.map((task) => task.id));

    // Calculate sub-task counts
    const currentCompletedSubTasksCount = completedSubTasks.filter((task) =>
      completedParentTaskIds.has(task?.parentTaskId!)
    ).length;

    // Calculate subtask with open parent task count
    const uncompletedParentTaskCount = completedSubTasks.filter((task) =>
      openParentTaskIds.has(task?.parentTaskId!)
    ).length;

    // Calculate subtask with deleted parent task count
    const deletedParentTaskCount = completedSubTasks.filter(
      (task) => !allParentTaskIds.has(task?.parentTaskId!)
    ).length;

    // Calculate final counts
    const completedTaskTotal = completedTasks.length + completedSubTasks.length - cancelledTasks.length;

    const hiddenTaskCount =
      completedTaskTotal -
      currentCompletedParentTasks.length -
      currentCompletedSubTasksCount -
      uncompletedParentTaskCount -
      deletedParentTaskCount;

    setTotalCompletedTask(completedTaskTotal);
    setHiddenTask(hiddenTaskCount);
  }, [projectData?.filteredProjectTasks?.filteredTasks, projectData?.tasks]);

  useEffect(() => {
    if (
      projectData?.taskFilterType?.taskViewType === TASK_LIST_TYPE.TASKS &&
      projectData?.taskFilterType?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS
    ) {
      calculateTaskCount();
    }
  }, [calculateTaskCount, projectData?.taskFilterType?.groupingStatus, projectData?.taskFilterType?.taskViewType]);
  const loadTaskData = useCallback(async () => {
    const [projectDetails] = await Promise.all([
      dispatch(getCurrentProjectDetails(workspace?.id, params?.id)),
      syncTasks({ loadCached: false }),
      dispatch(getTaskGroupList(params?.id, false, false))
    ]);
    if (projectData?.id !== params?.id) {
      rootStore.projectStore.setCurrentProject(params?.id);
    }
    if (projectDetails?.isMilestone) {
      dispatch(getMilestoneList(params?.id, false, false));
    }
    const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
    const validUser = projectDetails?.users?.find((user: any) => user?.id === currentUser?.id);
    if (!validUser) {
      history.push('/');
      return;
    }
  }, [dispatch, history, params?.id, projectData?.id, workspace?.id]);

  const loadWeekWiseTasks = useCallback(async () => {
    //Load week wise tasks
    const weekWiseTasks = rootStore?.projectStore?.currentProject?.weekWiseTasks;
    setWeekWiseTasks(weekWiseTasks || []);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (!taskId) {
        if (workspace?.id) {
          // Get task filter from user preference
          const currentProjectFilter = await getWorkspaceProjectPreferenceData(params?.id, 'tasks_filter_type');
          const viewType = currentProjectFilter?.viewType ?? TASK_LIST_TYPE.TASKS;
          await loadTaskData();
          rootStore.projectStore.currentProject?.updateProjectFilter();

          if (viewType === TASK_LIST_TYPE.CALANDAR_VIEW) {
            await loadWeekWiseTasks();
          }
        }
      }
      setLoading(false);
    } catch (e) {
      captureException(e);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, workspace?.id, params?.id, dispatch, history]);

  const onChangeFilterType = useCallback(async () => {
    if (workspace?.id && params?.id) {
      await loadTaskData();
    }
  }, [loadTaskData, params?.id, workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setTaskGroupsList([]));
      dispatch(setTasksList([]));
      dispatch(setOtherTaskList({ tasks: [], completedTasks: [] }));
      dispatch(setFilteredTasksList([]));
    };
  }, [dispatch]);

  const getTaskView = useMemo(() => {
    const taskViewType = projectData?.taskFilterType?.taskViewType || TASK_LIST_TYPE.TASKS;
    if (taskViewType === TASK_LIST_TYPE.TASK_GRID) {
      return <TaskGrid propsLoading={loading} loadData={loadData} />;
    }
    if (taskViewType === TASK_LIST_TYPE.CALANDAR_VIEW) {
      return (
        <CalendarView
          propsLoading={loading}
          loadData={loadData}
          weekWiseTasks={weekWiseTasks || []}
          loadWeekWiseTasks={loadWeekWiseTasks}
          currentDate={projectData?.startOfTheWeek || new Date()}
        />
      );
    }
    return (
      <TasksPage
        propsLoading={loading}
        loadData={loadData}
        completedTask={totalCompletedTask}
        hiddenTask={hiddenTask}
      />
    );
  }, [
    projectData?.taskFilterType?.taskViewType,
    projectData?.startOfTheWeek,
    loading,
    loadData,
    totalCompletedTask,
    hiddenTask,
    weekWiseTasks,
    loadWeekWiseTasks
  ]);

  return (
    <>
      <AppLayout isPadding={true}>
        <Group>
          {taskId ? (
            <TaskDetail />
          ) : (
            <>
              <TaskFormation>
                <Accounting
                  otherTaskList={() => onChangeFilterType()}
                  loadTaskList={loadData}
                  loadWeekWiseTasks={loadWeekWiseTasks}
                  isLeftPadding={true}
                  isProjectTasks={true}
                />
                {getTaskView}
              </TaskFormation>
            </>
          )}
        </Group>
      </AppLayout>
    </>
  );
};

export default observer(Tasks);
